import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { createNewsletter, getPresets } from '../services/newsletters';
import { useToast } from '../hooks/useToast';

const isoDate = (d) => d.toISOString().slice(0, 10);

const NewsletterCompose = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill;
  const { showError } = useToast();

  const today = new Date();
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  const [presets, setPresets] = useState([]);
  const [presetsLoaded, setPresetsLoaded] = useState(false);
  const [mode, setMode] = useState('preset');
  const [presetId, setPresetId] = useState('');
  const [themeName, setThemeName] = useState(prefill?.theme_name ?? '');
  const [themeIntent, setThemeIntent] = useState(prefill?.theme_intent ?? '');
  const [language, setLanguage] = useState(prefill?.language ?? 'en');
  const [windowStart, setWindowStart] = useState(
    prefill?.window_start ? isoDate(new Date(prefill.window_start)) : isoDate(weekAgo)
  );
  const [windowEnd, setWindowEnd] = useState(
    prefill?.window_end ? isoDate(new Date(prefill.window_end)) : isoDate(today)
  );
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getPresets()
      .then((data) => {
        const list = data.presets || [];
        setPresets(list);
        if (prefill) {
          setMode('inline');
        } else if (list.length === 0) {
          setMode('inline');
        }
      })
      .catch(() => { setPresets([]); setMode('inline'); })
      .finally(() => setPresetsLoaded(true));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setConflict(false);

    if (!windowStart || !windowEnd) {
      setError('Window start and end dates are required.');
      return;
    }
    const body = {
      language,
      window_start: new Date(`${windowStart}T00:00:00`).toISOString(),
      window_end: new Date(`${windowEnd}T23:59:59`).toISOString(),
    };

    if (mode === 'preset') {
      if (!presetId) { setError('Please select a preset.'); return; }
      body.preset_id = presetId;
    } else {
      if (!themeName.trim() || !themeIntent.trim()) {
        setError('Theme name and intent are required.');
        return;
      }
      body.theme_name = themeName.trim();
      body.theme_intent = themeIntent.trim();
    }

    setSubmitting(true);
    try {
      const created = await createNewsletter(body);
      navigate(`/newsletters/${created.id}`);
    } catch (err) {
      if (err.response?.status === 409) {
        setConflict(true);
      } else {
        const raw = err.response?.data?.detail;
        const msg = Array.isArray(raw)
          ? raw.map((e) => e.msg || JSON.stringify(e)).join('; ')
          : (raw || err.message || 'Failed to start generation');
        setError(msg);
        showError(msg);
      }
      setSubmitting(false);
    }
  };

  const pageTitle = prefill ? 'Regenerate newsletter' : 'New newsletter';

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <div className="bg-surface border-b-3 border-clay-border shadow-clay-sm">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center">
            <Link to="/newsletters" className="btn-clay-ghost mr-4 p-2" aria-label="Back to newsletters">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <h1 className="font-display text-2xl font-bold text-heading flex items-center gap-2">
                <span role="img" aria-hidden="true">✏️</span>
                {pageTitle}
              </h1>
              <p className="text-sm text-secondary-text mt-1">
                Generate a curated newsletter from your reading.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="card-clay p-6">
          {conflict && (
            <div className="card-clay border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 mb-4 p-3 text-sm">
              Another generation is already running.{' '}
              <Link to="/newsletters" className="underline font-medium">View newsletters →</Link>
            </div>
          )}
          {error && (
            <div className="card-clay border-rose-300 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 mb-4 p-3 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {presetsLoaded && presets.length > 0 && !prefill && (
              <div className="flex gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => setMode('preset')}
                  className={mode === 'preset' ? 'btn-clay' : 'btn-clay-ghost'}
                >
                  Use saved theme
                </button>
                <button
                  type="button"
                  onClick={() => setMode('inline')}
                  className={mode === 'inline' ? 'btn-clay' : 'btn-clay-ghost'}
                >
                  Inline theme
                </button>
              </div>
            )}

            {mode === 'preset' ? (
              <label className="block">
                <span className="text-sm font-medium text-body-text">Theme preset</span>
                <select
                  value={presetId}
                  onChange={(e) => setPresetId(e.target.value)}
                  className="input-clay mt-1 w-full"
                >
                  <option value="">— select a preset —</option>
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
            ) : (
              <>
                <label className="block">
                  <span className="text-sm font-medium text-body-text">Theme name</span>
                  <input
                    type="text"
                    value={themeName}
                    onChange={(e) => setThemeName(e.target.value)}
                    maxLength={255}
                    required
                    className="input-clay mt-1 w-full"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-body-text">Theme intent</span>
                  <textarea
                    value={themeIntent}
                    onChange={(e) => setThemeIntent(e.target.value)}
                    required
                    rows={4}
                    className="input-clay mt-1 w-full"
                    placeholder="What stories belong in this newsletter?"
                  />
                </label>
              </>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block">
                <span className="text-sm font-medium text-body-text">Language</span>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="input-clay mt-1 w-full"
                >
                  <option value="en">English</option>
                  <option value="zh">Chinese</option>
                </select>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-body-text">Window start</span>
                <input
                  type="date"
                  value={windowStart}
                  onChange={(e) => setWindowStart(e.target.value)}
                  required
                  className="input-clay mt-1 w-full"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-body-text">Window end</span>
                <input
                  type="date"
                  value={windowEnd}
                  onChange={(e) => setWindowEnd(e.target.value)}
                  required
                  className="input-clay mt-1 w-full"
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Link to="/newsletters" className="btn-clay-ghost text-sm">Cancel</Link>
              <button type="submit" disabled={submitting} className="btn-clay">
                {submitting ? 'Starting…' : 'Generate'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewsletterCompose;
